import { useState } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ReferralStatsBar from "./components/ReferralStatsBar";
import ReferralLinkCard from "./components/ReferralLinkCard";
import { mockReferralLinks } from "@/mocks/cart";
import SEO from "@/components/base/SEO";

type SortOption = "clicks" | "earnings" | "conversions" | "recent";

export default function ReferralsPage() {
  const [sortBy, setSortBy] = useState<SortOption>("clicks");
  const [searchQuery, setSearchQuery] = useState("");
  const [payoutModalOpen, setPayoutModalOpen] = useState(false);

  const totalClicks = mockReferralLinks.reduce((s, l) => s + l.clicks, 0);
  const totalConversions = mockReferralLinks.reduce((s, l) => s + l.conversions, 0);
  const totalEarnings = mockReferralLinks.reduce((s, l) => s + l.earnings, 0);
  const pendingEarnings = mockReferralLinks.reduce((s, l) => s + l.pendingEarnings, 0);

  const filtered = mockReferralLinks
    .filter((l) => !searchQuery || l.productTitle.toLowerCase().includes(searchQuery.toLowerCase()) || l.seller.toLowerCase().includes(searchQuery.toLowerCase()))
    .sort((a, b) => {
      if (sortBy === "clicks") return b.clicks - a.clicks;
      if (sortBy === "earnings") return b.earnings - a.earnings;
      if (sortBy === "conversions") return b.conversions - a.conversions;
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#f5f5f5" }}>
      <SEO title="My Referral Links — StarBookPrime" description="Track your referral link clicks, conversions, and commission earnings." canonical="/referrals" noIndex={true} />
      <Navbar />
      <div className="pt-16">
        {/* Banner */}
        <div className="relative h-40 md:h-52 overflow-hidden">
          <img
            src="https://public.readdy.ai/ai/img_res/77c17bd041d0d64ce33226876ac02cf6.jpg"
            alt="Referral Dashboard"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-black/30 flex items-center px-8 md:px-16">
            <div>
              <h1 className="text-2xl md:text-3xl font-extrabold text-white mb-1">My Referral Links</h1>
              <p className="text-white/70 text-sm">Track clicks, conversions, and earnings per product link</p>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
          {/* Stats */}
          <ReferralStatsBar
            totalClicks={totalClicks}
            totalConversions={totalConversions}
            totalEarnings={totalEarnings}
            pendingEarnings={pendingEarnings}
          />

          {/* Payout Banner */}
          <div className="bg-gradient-to-r from-orange-500 to-amber-500 rounded-2xl p-5 mb-8 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 flex items-center justify-center bg-white/20 rounded-xl">
                <i className="ri-money-dollar-circle-line text-white text-2xl" />
              </div>
              <div>
                <p className="text-white font-bold text-lg">${pendingEarnings.toFixed(2)} ready to withdraw</p>
                <p className="text-white/80 text-xs">Paid via PayPal within 3-5 business days</p>
              </div>
            </div>
            <button
              onClick={() => setPayoutModalOpen(true)}
              className="px-6 py-2.5 bg-white text-orange-600 text-sm font-bold rounded-full hover:bg-orange-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              Request Payout
            </button>
          </div>

          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-3 mb-6">
            <div className="relative flex-1">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by product or seller..."
                className="w-full pl-9 pr-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm outline-none focus:border-orange-400 transition-colors"
              />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500 whitespace-nowrap">Sort by:</span>
              {(["clicks", "earnings", "conversions", "recent"] as SortOption[]).map((opt) => (
                <button
                  key={opt}
                  onClick={() => setSortBy(opt)}
                  className={`px-3 py-2 rounded-lg text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap capitalize ${sortBy === opt ? "bg-orange-500 text-white" : "bg-white border border-gray-200 text-gray-600 hover:border-orange-300"}`}
                >
                  {opt}
                </button>
              ))}
            </div>
          </div>

          {/* Links Grid */}
          {filtered.length === 0 ? (
            <div className="bg-white rounded-2xl border border-gray-100 p-16 text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-gray-50 rounded-full mx-auto mb-4">
                <i className="ri-link-m text-3xl text-gray-300" />
              </div>
              <p className="text-gray-500 font-medium">No referral links found</p>
              <p className="text-xs text-gray-400 mt-1">Go to a factory page and click &quot;Get Link&quot; to start earning</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              {filtered.map((link) => (
                <ReferralLinkCard key={link.id} link={link} />
              ))}
            </div>
          )}

          {/* How It Works */}
          <div className="mt-12 bg-white rounded-2xl border border-gray-100 p-8">
            <h3 className="text-base font-bold text-gray-900 mb-6 text-center">How Referral Earnings Work</h3>
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-6">
              {[
                { step: "1", icon: "ri-store-line", title: "Pick a Product", desc: "Browse factory pages and choose products with the best commission rates" },
                { step: "2", icon: "ri-link-m", title: "Get Your Link", desc: "Click 'Get Link' to generate your unique referral URL with your code" },
                { step: "3", icon: "ri-share-line", title: "Share Everywhere", desc: "Post to TikTok, Instagram, YouTube, or any platform your followers use" },
                { step: "4", icon: "ri-money-dollar-circle-line", title: "Earn Commission", desc: "Get paid via PayPal for every sale made through your referral link" },
              ].map((s) => (
                <div key={s.step} className="text-center">
                  <div className="w-12 h-12 flex items-center justify-center bg-orange-50 rounded-xl mx-auto mb-3">
                    <i className={`${s.icon} text-orange-500 text-xl`} />
                  </div>
                  <p className="text-sm font-bold text-gray-900 mb-1">{s.title}</p>
                  <p className="text-xs text-gray-400 leading-relaxed">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Payout Modal */}
      {payoutModalOpen && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setPayoutModalOpen(false)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-base font-bold text-gray-900">Request Payout</h3>
              <button onClick={() => setPayoutModalOpen(false)} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer">
                <i className="ri-close-line text-xl" />
              </button>
            </div>
            <div className="bg-orange-50 rounded-xl p-4 mb-5">
              <p className="text-xs text-gray-500 mb-1">Available Balance</p>
              <p className="text-2xl font-extrabold text-orange-600">${pendingEarnings.toFixed(2)}</p>
            </div>
            <div className="mb-4">
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">PayPal Email</label>
              <input type="email" defaultValue="jessica@example.com" className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:border-orange-400 transition-colors" />
            </div>
            <div className="mb-5">
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Amount</label>
              <input type="text" defaultValue={`$${pendingEarnings.toFixed(2)}`} className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:border-orange-400 transition-colors" />
            </div>
            <p className="text-xs text-gray-400 mb-5">Payouts are processed within 3-5 business days. Minimum payout is $10.00.</p>
            <button
              onClick={() => setPayoutModalOpen(false)}
              className="w-full py-3 bg-orange-500 text-white text-sm font-bold rounded-full hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              Confirm Payout Request
            </button>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
